import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, User, ChevronDown } from 'lucide-react';
import AIChatbot from '@/components/AIChatbot';

const PRIMARY_NAV = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About Us' },
  { path: '/projects', label: 'Projects' },
  { path: '/testimonials', label: 'Testimonials' },
  { path: '/contact', label: 'Contact' },
];

const MORE_NAV = [
  { path: '/news-media', label: 'News & Media' },
  { path: '/social-activities', label: 'Social Activities' },
  { path: '/install', label: 'Get App' },
  { path: '/qr-code', label: 'QR Code' },
];

const ALL_NAV = [...PRIMARY_NAV, ...MORE_NAV];

const PublicLayout = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  const isActive = (path) => location.pathname === path;
  const moreActive = MORE_NAV.some(l => isActive(l.path));

  return (
    <div className="min-h-screen bg-white">

      {/* ── NAVBAR ── */}
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#0D1E2C] shadow-xl'
            : 'bg-[#0D1E2C]/95 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
            <div className="bg-white rounded-xl px-2 py-1 shadow-sm flex items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/57kg0gau_DBK%20LOGO%20Final.png"
                alt="DBK Group"
                className="h-9 w-auto"
              />
            </div>
            <div className="hidden sm:block">
              <p className="text-white font-bold text-sm leading-tight tracking-wide">DBK Group</p>
              <p className="text-[#C9A84C] text-[10px] leading-tight tracking-widest font-medium">OF COMPANIES</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {PRIMARY_NAV.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(link.path)
                    ? 'text-[#C9A84C] bg-white/5'
                    : 'text-stone-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* More Dropdown */}
            <div ref={moreRef} className="relative">
              <button
                onClick={() => setMoreOpen(v => !v)}
                className={`flex items-center gap-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                  moreActive ? 'text-[#C9A84C] bg-white/5' : 'text-stone-300 hover:text-white hover:bg-white/5'
                }`}
              >
                More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreOpen && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-[#0D1E2C] border border-white/10 rounded-lg shadow-2xl overflow-hidden">
                  {MORE_NAV.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMoreOpen(false)}
                      className={`block px-4 py-2.5 text-sm transition-colors ${
                        isActive(link.path)
                          ? 'text-[#C9A84C] bg-white/5'
                          : 'text-stone-300 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <Link
              to="/signin"
              data-testid="nav-signin-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium text-white border border-white/20 hover:border-[#C9A84C] hover:text-[#C9A84C] transition-all"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign In
            </Link>
            <Link
              to="/signup"
              data-testid="nav-signup-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium bg-[#C9A84C] hover:bg-[#b8962f] text-[#0D1E2C] font-semibold transition-all"
            >
              <User className="w-3.5 h-3.5" /> Sign Up
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(v => !v)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden bg-[#0D1E2C] border-t border-white/10">
            <div className="px-4 py-4 flex flex-col gap-1">
              {ALL_NAV.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-2.5 rounded text-sm font-medium ${
                    isActive(link.path) ? 'text-[#C9A84C] bg-white/5' : 'text-stone-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-white/10">
                <Link to="/signin" className="flex items-center justify-center gap-2 px-4 py-2.5 rounded text-sm font-medium text-white border border-white/20">
                  <LogIn className="w-4 h-4" /> Sign In
                </Link>
                <Link to="/signup" className="flex items-center justify-center gap-2 px-4 py-2.5 rounded text-sm font-semibold bg-[#C9A84C] text-[#0D1E2C]">
                  <User className="w-4 h-4" /> Sign Up
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      <Outlet />

      {/* AI Chatbot - bottom right on all public pages */}
      <AIChatbot context="public" />

      {/* ── FOOTER ── */}
      <footer data-testid="footer" className="bg-[#0D1E2C] text-stone-100 pt-14 pb-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-10 border-b border-white/10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-white rounded-xl px-2 py-1 shadow-sm flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_realestate-cms-15/artifacts/57kg0gau_DBK%20LOGO%20Final.png"
                    alt="DBK Group"
                    className="h-10 w-auto"
                  />
                </div>
                <div>
                  <p className="text-white font-bold text-base">DBK Group of Companies</p>
                  <p className="text-[#C9A84C] text-xs tracking-widest">REAL ESTATE</p>
                </div>
              </div>
              <p className="text-stone-400 text-sm leading-relaxed">
                A premier name in real estate, dedicated to transforming visions into landmark realities.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {ALL_NAV.map((link) => (
                  <Link key={link.path} to={link.path} className="text-stone-400 hover:text-[#C9A84C] text-sm transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Us</h4>
              <div className="space-y-2 text-sm text-stone-400">
                <p>info@siddhivinayakinfra.estate</p>
                <p>support@siddhivinayakinfra.estate</p>
                <p>+91 8625009659</p>
                <p className="leading-relaxed">Office No. BR2-330A to 334, 3rd Floor,<br/>Jai Ganesh Vision, Akurdi, Pune – 411035</p>
              </div>
            </div>
          </div>
          <div className="pt-6 text-center text-stone-500 text-sm">
            &copy; 2026 DBK Group Of Companies. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
